import { useEffect, useState } from "react";
import { Lightbulb, ArrowUpRight } from "lucide-react";
import api from "../lib/api";

// Priority palette. Softer red-tinted amber for high-priority items so the
// dashboard reads calm-but-actionable rather than alarming. Green means
// "everything healthy — proactive tip", amber is "worth a look but not urgent",
// red is "this needs attention today".
const PRIORITY_STYLES = {
  red:   { pill: "bg-amber-50 text-amber-900 border-amber-200",     icon: "text-amber-700",   dot: "bg-amber-500",   label: "Worth doing today" },
  amber: { pill: "bg-amber-50 text-amber-800 border-amber-200",     icon: "text-amber-600",   dot: "bg-amber-500",   label: "Worth doing today" },
  green: { pill: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: "text-emerald-600", dot: "bg-emerald-500", label: "A small thing worth doing" },
};

/**
 * "One Thing Worth Doing Today" — the signature dashboard widget.
 *
 * Renders a single, contextual recommendation derived from the customer's
 * most recent Mission Watch Score. Deliberately shows exactly ONE next step
 * — the point is to reduce decision fatigue and reinforce the trusted-partner
 * (not alarm-system) positioning.
 *
 * Hides itself entirely when there's no report yet, so a fresh-signup dashboard
 * stays clean.
 */
export function OneThingCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/one-thing").then(({ data }) => {
      if (!cancelled) setData(data);
    }).catch(() => {
      // Silent — the dashboard has plenty of other signals; hiding is fine.
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Silent-hide when no data or not yet available (fresh signup).
  if (loading || !data || !data.available) return null;

  const style = PRIORITY_STYLES[data.priority] || PRIORITY_STYLES.amber;

  return (
    <section
      className="rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden"
      data-testid="one-thing-card"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl bg-charcoal-900 grid place-items-center shrink-0`}>
            <Lightbulb className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full border ${style.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                {style.label}
              </span>
            </div>
            <h3
              className="text-lg sm:text-xl font-semibold text-charcoal-900 tracking-tight leading-snug"
              data-testid="one-thing-title"
            >
              {data.title}
            </h3>
            <p
              className="mt-2 text-sm text-charcoal-600 leading-relaxed"
              data-testid="one-thing-body"
            >
              {data.body}
            </p>
            <div className="mt-4 flex items-center gap-3 text-xs text-charcoal-500">
              <span className="font-mono">Based on your latest score of {data.score}/100</span>
              <a
                href="#health-report"
                className="inline-flex items-center gap-1 text-charcoal-900 font-medium hover:underline"
                data-testid="one-thing-see-report"
              >
                See full report <ArrowUpRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
